import Head from 'next/head'
import { useEffect } from 'react'
import ProductSeo from '@/Components/seo/ProductSeo'
import { PRODUCTS } from '@/lib/products'
import CoreCapabilities from "@/Components/SecureFlow/CoreCapabilities";
import CTASection from "@/Components/SecureFlow/CTASection";
import FAQSection, { FAQS } from "@/Components/SecureFlow/FAQSection";
import FraudSection from "@/Components/SecureFlow/FraudSection";
import Hero from "@/Components/SecureFlow/Hero";
import IndiaEcosystemSection from "@/Components/SecureFlow/IndiaEcosystemSection";
import InterceptSection from "@/Components/SecureFlow/InterceptSection";
import JetStreamSection from "@/Components/SecureFlow/JetStreamSection";
import SecureFlowLoader from "@/Components/SecureFlow/SecureFlowLoader";
import StepSection from "@/Components/SecureFlow/StepSection";
import TrustBand from "@/Components/SecureFlow/TrustBand";
import ContactPage from '@/Components/contactPage';

export default function Secureflow() {
    useEffect(() => {
        if (typeof window === 'undefined') return
        const ioIn = new IntersectionObserver(
            (es) => es.forEach((e) => { if (e.isIntersecting) e.target.classList.add('sf-in') }),
            { threshold: 0, rootMargin: '-10% 0px -10% 0px' }
        )
        const ioOut = new IntersectionObserver(
            (es) => es.forEach((e) => { if (!e.isIntersecting) e.target.classList.remove('sf-in') }),
            { threshold: 0 }
        )
        const targets = document.querySelectorAll('[data-sf-reveal]')
        targets.forEach((el) => { ioIn.observe(el); ioOut.observe(el) })
        return () => { ioIn.disconnect(); ioOut.disconnect() }
    }, [])

    return (
        <>
            <ProductSeo product={PRODUCTS.secureFlow} faqs={FAQS} />
            <Head>
                {/* Title and description come from lib/products.js via ProductSeo above */}
                <link rel="preconnect" href="https://fonts.googleapis.com" />
                <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="" />
                <link
                    href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&family=JetBrains+Mono:wght@400;500;600&display=swap"
                    rel="stylesheet"
                />
            </Head>
            <SecureFlowLoader />
            <div className="sf-root">
                <Hero/>
                <InterceptSection/>
                <StepSection/>
                <FraudSection/>
                <CoreCapabilities/>
                <JetStreamSection/>
                <IndiaEcosystemSection/>
                <FAQSection/>
                <TrustBand/>
                <div id="demo" style={{ scrollMarginTop: '80px' }}>
                    <ContactPage/>
                </div>
            </div>
            <style jsx global>{`
                .sf-root {
                    --ca-50:#F0F9FF;--ca-100:#E0F2FE;--ca-200:#BAE6FD;--ca-300:#7DD3FC;
                    --ca-400:#38BDF8;--ca-500:#0EA5E9;--ca-600:#0284C7;--ca-700:#0369A1;
                    --ca-800:#075985;--ca-900:#0C4A6E;
                    --ca-cyan:#06B6D4;--ca-cyan-light:#22D3EE;
                    --sf-50:#ECFDF5;--sf-100:#DCFCE7;--sf-200:#BBF7D0;--sf-300:#86EFAC;
                    --sf-400:#4ADE80;--sf-500:#22C55E;--sf-600:#16A34A;--sf-700:#15803D;
                    --bg:#FFFFFF;--bg-2:#F8FAFC;--bg-3:#F1F5F9;
                    --text:#0F172A;--text-2:#475569;--text-3:#64748B;--text-4:#94A3B8;
                    --line:rgba(15,23,42,0.07);--line-2:rgba(15,23,42,0.12);
                    --bg-dark:#050B19;--bg-dark-2:#0A1428;--bg-dark-3:#0F1B36;
                    --text-light:#E8F1FF;--text-light-2:rgba(232,241,255,0.70);
                    --text-light-3:rgba(232,241,255,0.45);
                    --line-dark:rgba(255,255,255,0.08);--line-dark-2:rgba(255,255,255,0.14);
                    --shadow-sm:0 1px 2px rgba(15,23,42,0.04);
                    --shadow:0 4px 16px rgba(15,23,42,0.06);
                    --shadow-lg:0 16px 48px rgba(15,23,42,0.08);
                    --shadow-blue:0 14px 40px -10px rgba(14,165,233,0.45);
                    --radius:12px;--radius-lg:18px;--radius-xl:28px;
                    --ease:cubic-bezier(0.22,1,0.36,1);
                    --ease-bounce:cubic-bezier(0.34,1.56,0.64,1);
                    --container:1240px;--gutter:clamp(20px,4vw,32px);
                    --sans:'Inter',-apple-system,BlinkMacSystemFont,system-ui,sans-serif;
                    --mono:'JetBrains Mono',ui-monospace,Menlo,monospace;
                    font-family: var(--sans);
                    color: var(--text);
                    line-height: 1.6;
                    -webkit-font-smoothing: antialiased;
                    -moz-osx-font-smoothing: grayscale;
                    letter-spacing: -0.011em;
                }
                .sf-root *,.sf-root *::before,.sf-root *::after { box-sizing: border-box; }
                .sf-root .sf-container {
                    max-width: var(--container);
                    margin: 0 auto;
                    padding: 0 var(--gutter);
                    position: relative;
                }
                .sf-root .sf-section { padding: clamp(72px,9vw,120px) 0; position: relative; }
                .sf-root .sf-section-header { text-align: center; max-width: 760px; margin: 0 auto clamp(48px,7vw,76px); }
                .sf-root .sf-eyebrow {
                    display: inline-flex; align-items: center; gap: 10px;
                    font-family: var(--mono); font-size: 14px; font-weight: 600;
                    letter-spacing: 0.18em; text-transform: uppercase;
                    color: var(--ca-700); background: var(--ca-50);
                    border: 1px solid var(--ca-200);
                    padding: 10px 20px; border-radius: 999px;
                }
                .sf-root .sf-eyebrow .sf-pulse {
                    width: 8px; height: 8px; border-radius: 50%;
                    background: var(--ca-500); box-shadow: 0 0 0 5px rgba(14,165,233,0.18);
                    animation: sfPulse 2.4s ease-in-out infinite;
                }
                .sf-root .sf-eyebrow.green { color: var(--sf-700); background: var(--sf-50); border-color: var(--sf-100); }
                .sf-root .sf-eyebrow.green .sf-pulse { background: var(--sf-500); box-shadow: 0 0 0 4px rgba(34,197,94,0.15); }
                .sf-root .sf-eyebrow.dark { color: var(--ca-300); background: rgba(14,165,233,0.10); border-color: rgba(14,165,233,0.25); }
                .sf-root .sf-eyebrow.dark .sf-pulse { background: var(--ca-400); box-shadow: 0 0 0 4px rgba(14,165,233,0.18); }
                @keyframes sfPulse { 0%,100% { opacity: 0.6 } 50% { opacity: 1 } }

                .sf-root .sf-section-title {
                    margin-top: 18px;
                    font-size: clamp(2rem,4.2vw,3.5rem);
                    font-weight: 600;
                    letter-spacing: -0.025em;
                    line-height: 1.05;
                    color: var(--text);
                    text-wrap: balance;
                }
                .sf-root .sf-section-title em {
                    font-style: normal; font-weight: 500;
                    background: linear-gradient(120deg,var(--ca-600) 0%,var(--ca-cyan) 50%,var(--sf-600) 100%);
                    -webkit-background-clip: text; background-clip: text; color: transparent;
                }
                .sf-root .sf-section-lede {
                    margin: 18px auto 0; max-width: 60ch;
                    font-size: clamp(1rem,1.18vw,1.13rem);
                    line-height: 1.7; color: var(--text-2);
                    text-wrap: pretty;
                }

                .sf-root .sf-reveal {
                    opacity: 0; transform: translateY(24px);
                    transition: opacity 0.85s var(--ease), transform 0.85s var(--ease);
                }
                .sf-root .sf-reveal.sf-in { opacity: 1; transform: translateY(0); }
                .sf-root .sf-reveal-d1 { transition-delay: 0.08s }
                .sf-root .sf-reveal-d2 { transition-delay: 0.16s }
                .sf-root .sf-reveal-d3 { transition-delay: 0.24s }
                .sf-root .sf-reveal-d4 { transition-delay: 0.32s }

                .sf-root .sf-btn {
                    display: inline-flex; align-items: center; gap: 8px;
                    padding: 12px 22px; border-radius: 999px;
                    font-size: 14.5px; font-weight: 500;
                    border: 1px solid transparent; cursor: pointer;
                    transition: all 0.25s var(--ease);
                    font-family: inherit; white-space: nowrap;
                    position: relative; overflow: hidden; text-decoration: none;
                }
                .sf-root .sf-btn-lg { padding: 14px 28px; font-size: 15px; }
                .sf-root .sf-btn-blue { background: var(--ca-500); color: #fff; box-shadow: var(--shadow-blue); }
                .sf-root .sf-btn-blue:hover { background: var(--ca-600); box-shadow: 0 18px 50px -12px rgba(14,165,233,0.6); }
                .sf-root .sf-btn-blue::before {
                    content: ''; position: absolute; inset: 0;
                    background: linear-gradient(120deg,transparent 30%,rgba(255,255,255,0.45) 50%,transparent 70%);
                    transform: translateX(-100%); transition: transform 0.7s var(--ease);
                }
                .sf-root .sf-btn-blue:hover::before { transform: translateX(100%); }
                .sf-root .sf-btn-ghost-light {
                    background: rgba(255,255,255,0.06); color: var(--text-light);
                    border-color: rgba(255,255,255,0.18); backdrop-filter: blur(10px);
                }
                .sf-root .sf-btn-ghost-light:hover {
                    border-color: var(--ca-400); color: var(--ca-300);
                    background: rgba(255,255,255,0.10);
                }
                .sf-root .sf-btn-ghost { background: #fff; color: var(--text); border-color: var(--line-2); }
                .sf-root .sf-btn-ghost:hover { border-color: var(--ca-500); color: var(--ca-600); background: var(--ca-50); }
                .sf-root .sf-btn .sf-arrow { transition: transform 0.3s var(--ease); }
                .sf-root .sf-btn:hover .sf-arrow { transform: translateX(4px); }
            `}</style>
        </>
    )
}
